import API, { gatewayFetch } from 'helpers/api';
import { apiURL } from 'config/baseURL';
import {
	IDashboardIndicatorStore,
	IDashboardCompletionStore,
	IDashboardPassFailRatioStore,
	IDashboardLessonProgressStore,
	IDashboardLessonStatusStore,
	IDashboardSummaryStore,
	IDashboardQuizInSightStore,
	EntityStatusStore,
	OverallQuizInsights,
	adminOverallInsight,
	instructorOverallInsight,
	learnerOverallInsight,
	IDashboardtimeSpent,
	LmsDashboardTrends,
	LmsDashboardTopBottomEntities,
	LmsQuizAssociation,
	LmsEntityDetail,
	LmsEntityDetailResponse,
	LmsEntityEnrollmentStats,
	LmsEntityStatusWiseSplit,
	LmsUserGroupViewDetailsData,
	LmsCustomGroupViewDetailsData,
	LmsViewDetailsApiResponse,
	CustomGroupDrillResponse,
	UserGroupDrillResponse,
} from 'reducers/lms/lmsDashboard/lmsDashboard.type';
import { getJwtToken } from 'services/gallery/gallery.service';
import { store } from 'store/configStore';
import queryString from 'query-string';
import moment from 'moment';
import { getStartEpochTimeInMillisecondsFromUTC, getEndOfDayEpochTimeInMillisecondsFromUTC } from 'utils/getUTCEpoch';
import { DownloadDashboardFileType } from '@nimbly-technologies/nimbly-common/lib/enumerators';
import { downloadURL } from 'utils/download';
import { filterObjectWithEmptyValues } from 'pages/dashboardRevamp/utils/filterObjectWithEmptyValues';
import { getRequestOptions } from 'services/dashboardRevamp/issueInsights/issueInsights.service';
import { LmsTab } from 'pages/lms/lmsDashboardRevampPage/lmsDashboardUtils';
import { SubDrillByField } from 'pages/lms/lmsDashboardRevampPage/thirdRow/ViewDetailsModalUtils';

export enum RefetchDrillBy {
	None = 'none',
	UserGroup = 'userGroup',
	Learner = 'enrolledLearnersName',
}

interface ProcessedDashboardFilters {
	startDate?: number | string | null;
	endDate?: number | string | null;
	instructorIDs?: string[];
	courseIDs?: string[];
	lessonIDs?: string[];
	lessonStatus?: string[];
	userGroupIDs?: string[];
	customGroupIDs?: string[];
	assignmentType?: string[];
	typeOfEnrollment?: string[];
	entity?: string;
	limit?: number;
	sortDirections?: string;
	sortFields?: string;
	status?: string;
	type?: string;
	role?: string;
	period?: string;
	sortDirection?: string;
	page?: number;
	fieldName?: string[];
	fileType?: string;
}

const buildDashboardQueryParams = (): ProcessedDashboardFilters => {
	const dashboardFilters = store.getState().lmsDashboard.dashboardFilters;

	let filteredData: ProcessedDashboardFilters = filterObjectWithEmptyValues(dashboardFilters);
	if (filteredData.startDate) {
		const utcStartDate = getStartEpochTimeInMillisecondsFromUTC(moment(filteredData.startDate).format('YYYY-MM-DD'));
		filteredData = {
			...filteredData,
			startDate: utcStartDate,
		};
	}
	if (filteredData.endDate) {
		const utcEndDate = getEndOfDayEpochTimeInMillisecondsFromUTC(moment(filteredData.endDate).format('YYYY-MM-DD'));
		filteredData = {
			...filteredData,
			endDate: utcEndDate,
		};
	}

	if (filteredData?.assignmentType?.length && filteredData.assignmentType.length > 0) {
		const { assignmentType, ...rest } = filteredData;
		filteredData = {
			...rest,
			typeOfEnrollment: assignmentType,
		};
	}

	return filteredData;
};

export const fetchLmsDashboardIndicator = async (): Promise<IDashboardIndicatorStore | undefined> => {
	const url = `${apiURL}/lms/learner-activities/dashboard-indicator`;
	const res = await API.getJSON(url);
	return res?.data as IDashboardIndicatorStore;
};

export const fetchLmsDashboardCompletionStats = async (): Promise<IDashboardCompletionStore | undefined> => {
	try {
		const url = `${apiURL}/lms/dashboard/completion`;
		const res = await API.getJSON(url);
		return res?.data as IDashboardCompletionStore;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsDashboardPassFailRatio = async (): Promise<IDashboardPassFailRatioStore[] | undefined> => {
	try {
		const { startDate, endDate } = store.getState().lmsDashboard.lessonStatus;
		let query = '';
		if (startDate) {
			let utcStartDate = getStartEpochTimeInMillisecondsFromUTC(moment(startDate).format('YYYY-MM-DD'));

			query = `${query}&${queryString.stringify({
				startDate: utcStartDate,
			})}`;
		}
		if (endDate) {
			let utcEndDate = getEndOfDayEpochTimeInMillisecondsFromUTC(moment(endDate).format('YYYY-MM-DD'));
			query = `${query}&${queryString.stringify({
				endDate: utcEndDate,
			})}`;
		}
		const url = `${apiURL}/lms/dashboard/pass-fail-ratio?${query}`;
		const res = await API.getJSON(url);
		return res?.data as IDashboardPassFailRatioStore[];
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsDashboardLessonProgress = async (): Promise<IDashboardLessonProgressStore[] | undefined> => {
	try {
		const { startDate, endDate } = store.getState().lmsDashboard.lessonStatus;
		let query = '';
		if (startDate) {
			let utcStartDate = getStartEpochTimeInMillisecondsFromUTC(moment(startDate).format('YYYY-MM-DD'));

			query = `${query}&${queryString.stringify({
				startDate: utcStartDate,
			})}`;
		}
		if (endDate) {
			let utcEndDate = getEndOfDayEpochTimeInMillisecondsFromUTC(moment(endDate).format('YYYY-MM-DD'));
			query = `${query}&${queryString.stringify({
				endDate: utcEndDate,
			})}`;
		}

		let url = `${apiURL}/lms/dashboard/lesson-progress?${query}`;

		const res = await API.getJSON(url);
		return res?.data as IDashboardLessonProgressStore[];
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsDashboardLessonStatus = async (): Promise<IDashboardLessonStatusStore | undefined> => {
	try {
		const { startDate, endDate } = store.getState().lmsDashboard.lessonStatus;
		let query = '';
		if (startDate) {
			query = `${query}&${queryString.stringify({
				startDate,
			})}`;
		}
		if (endDate) {
			query = `${query}&${queryString.stringify({
				endDate,
			})}`;
		}
		let url = `${apiURL}/lms/dashboard/status?${query}`;

		const res = await API.getJSON(url);

		return res?.data as IDashboardLessonStatusStore;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsDashboardEntityStatus = async (entity?: string): Promise<EntityStatusStore> => {
	try {
		let query = buildDashboardQueryParams();
		if (entity) {
			query = {
				...query,
				entity,
			};
		}
		let url = `${apiURL}/lms/dashboard/status-v2`;

		const options = await getRequestOptions(query);
		const res = await gatewayFetch(url, options);
		const responseData = await res.json();

		return responseData?.data as EntityStatusStore;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsDashboardSummary = async (status?: string): Promise<IDashboardSummaryStore | undefined> => {
	try {
		const { limit, page, sortDirections, sortFields, type } = store.getState().lmsDashboard.summary;
		let query = buildDashboardQueryParams();
		query = {
			...query,
			limit,
			page,
		};

		if (sortDirections) {
			query = {
				...query,
				sortDirections,
			};
		}
		if (sortFields) {
			query = {
				...query,
				sortFields,
			};
		}
		if (status) {
			query = {
				...query,
				status,
			};
		}
		if (type) {
			query = {
				...query,
				type,
			};
		}
		const url = `${apiURL}/lms/dashboard/summary`;
		const options = await getRequestOptions(query);
		const res = await gatewayFetch(url, options);
		const responseData = await res.json();

		return responseData?.data?.data as IDashboardSummaryStore;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const downloadBulkSummaryReport = async (): Promise<any> => {
	try {
		const {
			limit,
			page,
			startDate,
			endDate,
			sortDirections,
			sortFields,
			status,
			type,
		} = store.getState().lmsDashboard.summary;
		let query = queryString.stringify({
			limit,
			page,
		});

		if (sortDirections) {
			query = `${query}&${queryString.stringify({
				sortDirections,
			})}`;
		}
		if (sortFields) {
			query = `${query}&${queryString.stringify({
				sortFields,
			})}`;
		}
		if (status) {
			query = `${query}&${queryString.stringify({
				status,
			})}`;
		}
		if (type) {
			query = `${query}&${queryString.stringify({
				type,
			})}`;
		}
		if (startDate) {
			query = `${query}&${queryString.stringify({
				startDate,
			})}`;
		}
		if (endDate) {
			query = `${query}&${queryString.stringify({
				endDate,
			})}`;
		}

		const url = `${apiURL}/lms/dashboard/summary-download?${query}`;

		const token = await getJwtToken(url);

		const response = (await gatewayFetch(url, {
			method: 'GET',
			headers: {
				authorization: token,
				deviceTImezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			},
		})) as any;
		if (response.status !== 200) {
			const json = await response.json();
			throw new Error(json.message);
		}

		const responseBlob = await response.blob();
		return responseBlob;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsDashboardQuizInsight = async (): Promise<IDashboardQuizInSightStore[] | undefined> => {
	try {
		const { startDate, endDate } = store.getState().lmsDashboard.lessonStatus;
		let query = '';
		if (startDate) {
			let utcStartDate = getStartEpochTimeInMillisecondsFromUTC(moment(startDate).format('YYYY-MM-DD'));

			query = `${query}&${queryString.stringify({
				startDate: utcStartDate,
			})}`;
		}
		if (endDate) {
			let utcEndDate = getEndOfDayEpochTimeInMillisecondsFromUTC(moment(endDate).format('YYYY-MM-DD'));
			query = `${query}&${queryString.stringify({
				endDate: utcEndDate,
			})}`;
		}
		const url = `${apiURL}/lms/dashboard/quiz-insight?${query}`;
		const res = await API.getJSON(url);
		return res?.data as IDashboardQuizInSightStore[];
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsDashboardQuizInsightV2 = async (entity?: string): Promise<IDashboardQuizInSightStore[]> => {
	try {
		let query = buildDashboardQueryParams();
		if (entity) {
			query = {
				...query,
				entity,
			};
		}
		const url = `${apiURL}/lms/dashboard/quiz-insight-v2`;
		const options = await getRequestOptions(query);
		const res = await gatewayFetch(url, options);
		const responseData = await res.json();

		return responseData?.data as IDashboardQuizInSightStore[];
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsDashboardOverallQuizInsight = async (): Promise<OverallQuizInsights> => {
	try {
		const query = buildDashboardQueryParams();

		const url = `${apiURL}/lms/dashboard/overall-quiz-insight`;
		const options = await getRequestOptions(query);
		const res = await gatewayFetch(url, options);
		const responseData = await res.json();
		return responseData?.data as OverallQuizInsights;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsDashboardOverallInsight = async (
	role: string,
): Promise<adminOverallInsight | instructorOverallInsight | learnerOverallInsight> => {
	try {
		let query = '';
		if (role) {
			query = `${query}&${queryString.stringify({
				role,
			})}`;
		}
		const url = `${apiURL}/lms/dashboard/overview?${query}`;
		const res = await API.getJSON(url);
		return res?.data as adminOverallInsight | instructorOverallInsight | learnerOverallInsight;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsDashboardTimeSpent = async (entity?: string, role?: string): Promise<IDashboardtimeSpent[]> => {
	try {
		let query = buildDashboardQueryParams();
		if (entity) {
			query = {
				...query,
				entity,
			};
		}
		if (role) {
			query = {
				...query,
				role,
			};
		}
		const url = `${apiURL}/lms/dashboard/quiz-insight-v2`;
		const options = await getRequestOptions(query);
		const res = await gatewayFetch(url, options);
		const responseData = await res.json();
		return responseData?.data as IDashboardtimeSpent[];
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsDashboardTrends = async ({
	role,
	period,
	entity,
}: {
	role: string;
	period: string;
	entity: string;
}): Promise<LmsDashboardTrends[]> => {
	try {
		let query = buildDashboardQueryParams();
		if (entity) {
			query = {
				...query,
				entity,
			};
		}
		if (role) {
			query = {
				...query,
				role,
			};
		}
		if (period) {
			query = {
				...query,
				period,
			};
		}
		const url = `${apiURL}/lms/dashboard/trends`;
		const options = await getRequestOptions(query);
		const res = await gatewayFetch(url, options);
		const responseData = await res.json();
		return responseData?.data?.result as LmsDashboardTrends[];
	} catch (error) {
		return Promise.reject(error);
	}
};

interface LmsDashboardTopBottomEntitiesRequest {
	role: string;
	entity: string;
	sortDirection: string;
	status?: string;
}

interface LmsDashboardTopBottomEntitiesRequestBody {
	// Date filters (in milliseconds epoch time)
	startDate: number; // Start of day in UTC milliseconds
	endDate: number; // End of day in UTC milliseconds

	// Entity type (mapped from frontend values)
	entity: 'learner_detail' | 'user_group' | 'custom_group' | 'course' | 'lesson';

	// User role
	role: string; // e.g., 'admin', 'instructor', 'learner'

	// Sort direction
	sortDirection: 'asc' | 'desc';

	// Status field (mapped from frontend values)
	status: 'completion_rate' | 'completed' | 'in_progress' | 'overdue' | 'score' | 'pass_fail_ratio';

	// Additional filters from dashboard (when present)
	instructorIDs?: string[];
	courseIDs?: string[];
	lessonIDs?: string[];
	lessonStatus?: string[];
	userGroupIDs?: string[];
	customGroupIDs?: string[];
	assignmentType?: string[];
}

export const fetchLmsDashboardTopBottomEntites = async ({
	role,
	entity,
	sortDirection,
	status = 'completion_rate',
}: LmsDashboardTopBottomEntitiesRequest): Promise<LmsDashboardTopBottomEntities[]> => {
	try {
		// Get all filters from dashboard
		const allFilters = buildDashboardQueryParams();

		// Map entity types to match API expectations
		const entityMap: Record<string, string> = {
			instructor: 'learner_detail',
			learner: 'learner_detail',
			userGroup: 'user_group',
			customGroup: 'custom_group',
			course: 'course',
			lesson: 'lesson',
		};

		const mappedEntity = entityMap[entity] || entity;

		// Map status values
		const statusMap: Record<string, string> = {
			completionRate: 'completion_rate',
			completed: 'completed',
			inProgress: 'in_progress',
			overdue: 'overdue',
			score: 'score',
			passFailRatio: 'pass_fail_ratio',
		};

		const mappedStatus = statusMap[status] || status;

		const requestBody = {
			...allFilters,
			entity: mappedEntity as LmsDashboardTopBottomEntitiesRequestBody['entity'],
			role,
			sortDirection: sortDirection as LmsDashboardTopBottomEntitiesRequestBody['sortDirection'],
			status: mappedStatus as LmsDashboardTopBottomEntitiesRequestBody['status'],
		};

		const url = `${apiURL}/lms/dashboard/top-bottom-entities`;
		const token = await getJwtToken(url);

		const res = await gatewayFetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: token,
				deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			},
			body: JSON.stringify(requestBody as LmsDashboardTopBottomEntitiesRequestBody),
		});

		const responseData = await res.json();
		return responseData?.data as LmsDashboardTopBottomEntities[];
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsEnrollmentMetrics = async ({
	role,
	entity,
}: {
	role: string;
	entity: string;
}): Promise<LmsDashboardTopBottomEntities[]> => {
	try {
		let query = buildDashboardQueryParams();
		if (entity) {
			query = {
				...query,
				entity,
			};
		}
		if (role) {
			query = {
				...query,
				role,
			};
		}
		const url = `${apiURL}/lms/dashboard/enrollment-metrics`;
		const options = await getRequestOptions(query);
		const res = await gatewayFetch(url, options);
		const responseData = await res.json();
		return responseData?.data as LmsDashboardTopBottomEntities[];
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsDashboardQuizAssociation = async (): Promise<LmsQuizAssociation[]> => {
	try {
		// TOD: Implement Pagination
		let query = queryString.stringify({
			limit: 5,
			page: 1,
		});
		const url = `${apiURL}/lms/dashboard/quiz-association`;
		const res = await API.getJSON(url);
		return res?.data as LmsQuizAssociation[];
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsEntityDetails = async (entity: string): Promise<LmsEntityDetailResponse> => {
	try {
		let query = buildDashboardQueryParams();
		const { page, limit } = store.getState().lmsDashboard.entityDetail;
		if (page) {
			query = {
				...query,
				page,
			};
		}
		if (limit) {
			query = {
				...query,
				limit,
			};
		}
		if (entity) {
			query = {
				...query,
				entity,
			};
		}

		const url = `${apiURL}/lms/dashboard/entity-details`;
		const options = await getRequestOptions(query);
		const res = await gatewayFetch(url, options);
		const responseData = await res.json();
		return responseData?.data as LmsEntityDetailResponse;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsEnrollmentStats = async (entity: string): Promise<LmsEntityEnrollmentStats> => {
	try {
		let query = buildDashboardQueryParams();
		if (entity) {
			query = {
				...query,
				entity,
			};
		}
		const url = `${apiURL}/lms/dashboard/enrollment-stats`;
		const options = await getRequestOptions(query);
		const res = await gatewayFetch(url, options);
		const responseData = await res.json();

		return responseData.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const fetchLmsEntityStatusWiseSplit = async (entity: string): Promise<LmsEntityStatusWiseSplit> => {
	try {
		let query = buildDashboardQueryParams();
		if (entity) {
			query = {
				...query,
				entity,
			};
		}
		const url = `${apiURL}/lms/dashboard/status-split`;
		const options = await getRequestOptions(query);
		const res = await gatewayFetch(url, options);
		const responseData = await res.json();

		return responseData.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const mapEntityToApiValue = (entity: LmsTab): string => {
	switch (entity) {
		case LmsTab.CustomGroup:
			return 'custom_group';
		case LmsTab.UserGroup:
			return 'user_group';
		case LmsTab.Learner:
			return 'learner';
		case LmsTab.Course:
			return 'course';
		case LmsTab.Lesson:
			return 'lesson';
		default:
			return entity;
	}
};

export const fetchLmsViewDetails = async ({
	role,
	entity,
	drillBy,
	subDrillBy,
	limit = 10, // API only support limit,but not pagination because this API especially made for preview only.
}: {
	role: string;
	entity: string;
	drillBy?: RefetchDrillBy;
	subDrillBy?: SubDrillByField;
	limit?: number;
}): Promise<{ data: (LmsUserGroupViewDetailsData | LmsCustomGroupViewDetailsData)[]; totalCount?: number }> => {
	try {
		const dashboardParams = buildDashboardQueryParams();

		const requestBody = {
			startDate: dashboardParams.startDate,
			endDate: dashboardParams.endDate,
			entity,
			role,
			drillBy,
			subDrillBy,
			limit,
		};

		const url = `${apiURL}/lms/dashboard/view-details`;
		const options = await getRequestOptions(requestBody);

		const res = await gatewayFetch(url, options);
		const responseData: LmsViewDetailsApiResponse = await res.json();
		return {
			data: responseData?.data || [],
			totalCount: responseData?.data?.length || 0,
		};
	} catch (error) {
		console.error('Error in fetchLmsViewDetails', error);
		return { data: [], totalCount: 0 };
	}
};

interface LmsEntityDetailsRequest {
	entity: string;
	limit: number;
	page: number;
	startDate?: number;
	endDate?: number;
	customGroupIds?: string[];
	customGroupIDs?: string[];
	departmentIds?: string[];
	userGroupIds?: string[];
	userGroupIDs?: string[];
	courseIds?: string[];
	courseIDs?: string[];
	lessonIds?: string[];
	lessonIDs?: string[];
	instructorIds?: string[];
	instructorIDs?: string[];
	learnerIds?: string[];
	typeOfEnrollment?: string;
	status?: string[];
	lessonStatus?: string[];
}

export const fetchLmsEntityDetailsTable = async ({
	entity,
	limit = 5,
	page = 1,
}: {
	entity: string;
	limit?: number;
	page?: number;
}): Promise<{ data: Record<string, any>[]; totalCount?: number }> => {
	try {
		const dashboardFilters = store.getState().lmsDashboard.dashboardFilters;
		let filteredData = filterObjectWithEmptyValues(dashboardFilters);

		let requestBody: LmsEntityDetailsRequest = {
			entity,
			limit,
			page,
		};

		if (filteredData.startDate) {
			requestBody.startDate = getStartEpochTimeInMillisecondsFromUTC(
				moment(filteredData.startDate).format('YYYY-MM-DD'),
			);
		}
		if (filteredData.endDate) {
			requestBody.endDate = getEndOfDayEpochTimeInMillisecondsFromUTC(
				moment(filteredData.endDate).format('YYYY-MM-DD'),
			);
		}

		if (filteredData.userGroupIDs?.length) {
			requestBody.userGroupIDs = filteredData.userGroupIDs;
		}
		if (filteredData.customGroupIDs?.length) {
			requestBody.customGroupIDs = filteredData.customGroupIDs;
		}
		if (filteredData.typeOfEnrollment) {
			requestBody.typeOfEnrollment = filteredData.typeOfEnrollment;
		}
		if (filteredData.instructorIDs?.length) {
			requestBody.instructorIDs = filteredData.instructorIDs;
		}
		if (filteredData.lessonIDs?.length) {
			requestBody.lessonIDs = filteredData.lessonIDs;
		}
		if (filteredData.courseIDs?.length) {
			requestBody.courseIDs = filteredData.courseIDs;
		}
		if (filteredData.status) {
			requestBody.status = filteredData.status;
		}
		if (filteredData.lessonStatus) {
			requestBody.lessonStatus = filteredData.lessonStatus;
		}

		const url = `${apiURL}/lms/dashboard/entity-details-table`;
		const token = await getJwtToken(url);

		const options = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				authorization: token,
			},
			body: JSON.stringify(requestBody),
		};

		const res = await gatewayFetch(url, options);
		const responseData = await res.json();

		return {
			data: responseData?.data?.result as Record<string, any>[],
			totalCount: responseData?.data?.totalCount ?? 0,
		};
	} catch (error) {
		console.error('Error in fetchLmsEntityDetailsTable', error);
		return { data: [], totalCount: 0 };
	}
};

export const fetchLmsDownloadReport = async ({
	role,
	entity,
	fieldName = [],
	fileType,
	otherFilters,
}: {
	role: string;
	entity: string;
	fieldName?: string[];
	fileType: DownloadDashboardFileType;
	otherFilters?: Record<string, any>;
}): Promise<void> => {
	try {
		let query = buildDashboardQueryParams();
		if (entity) {
			query = {
				...query,
				entity,
			};
		}
		if (role) {
			query = {
				...query,
				role,
			};
		}
		if (fieldName) {
			query = {
				...query,
				fieldName,
			};
		}
		if (otherFilters) {
			query = {
				...query,
				...otherFilters,
			};
		}
		if (fileType) {
			query = {
				...query,
				fileType,
			};
		}
		const url = `${apiURL}/lms/dashboard/download`;
		const options = await getRequestOptions(query);
		const response = await gatewayFetch(url, options);
		const { data, message } = await response.json();

		if (data && message === 'SUCCESS') {
			await downloadURL(data, `nimbly-lms-dashboard-${moment().format('DD-MM-YY')}.${fileType}`);
		}
	} catch (error) {
		console.error('Error in fetchViewDetailsData', error);
	}
};

export const fetchCustomGroupDrill = async ({
	customGroupID,
}: {
	customGroupID: string;
}): Promise<CustomGroupDrillResponse> => {
	try {
		let query = buildDashboardQueryParams();

		const requestBody = {
			startDate: query.startDate,
			endDate: query.endDate,
			customGroupID,
		};

		const url = `${apiURL}/lms/dashboard/custom-group-drill`;
		const options = await getRequestOptions(requestBody);

		const response = await gatewayFetch(url, options);
		const responseData = await response.json();
		return responseData.data as CustomGroupDrillResponse;
	} catch (error) {
		console.error('Error in fetchCustomGroupDrill', error);
		throw error;
	}
};

export const fetchUserGroupDrill = async ({
	userGroupID,
}: {
	userGroupID: string;
}): Promise<UserGroupDrillResponse> => {
	try {
		let query = buildDashboardQueryParams();

		const requestBody = {
			startDate: query.startDate,
			endDate: query.endDate,
			userGroupID,
		};

		const url = `${apiURL}/lms/dashboard/user-group-drill`;
		const options = await getRequestOptions(requestBody);

		const response = await gatewayFetch(url, options);
		const responseData = await response.json();
		return responseData.data as UserGroupDrillResponse;
	} catch (error) {
		console.error('Error in fetchUserGroupDrill', error);
		throw error;
	}
};
